import { ROUTES } from 'Constants';
import AnimatedSuccessCheckmark from 'Modules/Ui/AnimatedSuccessCheckmark';
import StyledQrCode from 'Views/Components/_HumanWritten/StyleQrCode';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { useNavigate } from 'react-router-dom';
import { store as globalStore } from 'Models/Store';
import { useAnytimeTicketStore, useAnytimeTicketStoreApi } from '../State/AnytimeTicketContext';
import { anytimeTicketInline, anytimeTicketsLabel } from 'Util/_HumanWritten/anytimeTicketTerm';

interface ContentProps {
	/** Closes the modal — provided by AnytimeTicketModal so the link can navigate away cleanly. */
	onClose?: () => void;
}

export default function AnytimeTicketSuccessStep({ onClose }: ContentProps) {
	const userEntitlementId = useAnytimeTicketStore(s => s.userEntitlementId);
	const productType = useAnytimeTicketStore(s => s.selectedProductType);
	const selectedCustomerId = useAnytimeTicketStore(s => s.selectedCustomerId);
	const selectedCustomer = useAnytimeTicketStore(s => s.selectedCustomer);
	const navigate = useNavigate();

	const isTicket = productType === 'TICKET';

	// Staff selling on behalf of a customer → that customer's account; a customer buying for
	// themselves → their own account. Both land on the Anytime Tickets ("entitlements") tab.
	const isStaff = globalStore.isStaff;
	const accountEntitlementsPath =
		isStaff && selectedCustomerId
			? ROUTES.USER_DETAIL.url(selectedCustomerId, 'entitlements')
			: '/account-details/entitlements';

	const customerFirstName = selectedCustomer?.firstName?.trim();
	const ticketsTerm = anytimeTicketsLabel();
	let viewTicketsLabel = `View your ${ticketsTerm}`;
	if (isStaff) {
		viewTicketsLabel = customerFirstName
			? `View ${customerFirstName}'s ${ticketsTerm}`
			: `View customer's ${ticketsTerm}`;
	}

	const goToAnytimeTickets = () => {
		// Close the modal first so it doesn't linger over the account page, then navigate.
		onClose?.();
		navigate(accountEntitlementsPath);
	};

	return (
		<div className="flex flex-col items-center justify-center text-center" data-test-id="anytime-ticket-success">
			<AnimatedSuccessCheckmark size="5rem" className="mb-4" />

			<h4 className="mb-4 text-3xl font-extrabold tracking-tight text-gray-900">Purchase complete!</h4>

			{isTicket && (
				<>
					<p className="max-w-md mx-auto mb-10 text-xl leading-relaxed text-gray-600">
						Your {anytimeTicketInline()} has been added to your account.
					</p>

					<StyledQrCode value={`${window.location.origin}/qr-code/anytime-ticket/${userEntitlementId}`} />

					<Button
						display={Display.Text}
						colors={Colors.Primary}
						newStyles
						className="mt-6"
						onClick={goToAnytimeTickets}
						buttonProps={{ 'data-test-id': 'anytime-ticket-success-view-account' } as any}
					>
						{viewTicketsLabel}
					</Button>
				</>
			)}
		</div>
	);
}

interface FooterProps {
	onCancel: () => void;
	onDone: () => void;
	scrollToTop: () => void;
}

AnytimeTicketSuccessStep.Footer = function AnytimeTicketSuccessFooter({ onDone }: FooterProps) {
	const storeApi = useAnytimeTicketStoreApi();

	const handleBuyAnother = () => {
		storeApi.getState().resetStore();
	};

	return (
		<NewModal.Footer>
			<Button
				display={Display.Outline}
				colors={Colors.Primary}
				newStyles
				onClick={handleBuyAnother}
				buttonProps={{ 'data-test-id': 'anytime-ticket-success-buy-another' } as any}
			>
				Buy another
			</Button>
			<Button
				display={Display.Solid}
				colors={Colors.Primary}
				newStyles
				onClick={onDone}
				buttonProps={{ 'data-test-id': 'anytime-ticket-success-done' } as any}
			>
				Done
			</Button>
		</NewModal.Footer>
	);
};
